import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { useDeletePlaylist } from '@hooks/playlist/useDeletePlaylist'
import { useFetchPlaylists } from '@hooks/useFetchPlaylists'
import { useMountEffect } from '@hooks/useMountEffect'
import { usePlaylistFilter } from '@hooks/playlist/usePlaylistFilter'
import { useShowOnDesktop } from '@hooks/useShowOnDesktop'
import { useScrollToHighlight } from '@hooks/useScrollToHighlight'
import { FetchUserPlaylistsPlaylistTypeEnum as playlistTypeEnum } from '@swagger'
import { Page } from '@components/Elements/PageMargins'
import LoadingProgress from '@components/Elements/LoadingProgress'
import DeletePlaylistConfirmationModal from '@components/Modals/playlist/DeletePlaylistConfirmationModal'
import RemoveSharedPlaylistModal from '@components/Modals/playlist/RemoveSharedPlaylistModal'
import ContentDetailPlaylistCard from '@components/Playlist/ContentDetailPlaylistCard'
import EmptyPlaylist from '@components/Playlist/EmptyPlaylist'
import NoFilterResultsPlaylist from '@components/Playlist/NoFilterResultsPlaylist'
import DropDown, { DropDownVariant } from '@components/Menus/DropDown'
import { PLAYLIST_FILTER_OPTIONS } from '@utils/searchAndFilterEnums'
import { useCopyPlaylist } from '@hooks/playlist/useCopyPlaylist'
import { useRemoveSharedPlaylist } from '@hooks/playlist/useRemoveSharedPlaylist'
import { useUser } from 'UserContext'

interface PlaylistGridProps {
  playlistType: playlistTypeEnum
  basePath: string
}

const PlaylistGrid: React.FC<PlaylistGridProps> = ({
  playlistType,
  basePath,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const showOnDesktop = useShowOnDesktop()
  const { user } = useUser()
  const [playlistKeyToDelete, setPlaylistKeyToDelete] = useState<number>()
  const [sharedPlaylistToRemove, setSharedPlaylistToRemove] = useState<{
    key: number
    name: string
  }>()

  const { playlistsContent, triggerFetch, isLoading } = useFetchPlaylists({
    playlistType,
  })

  const { filterType, handleFilterSelection, filteredPlaylists } =
    usePlaylistFilter({ playlists: playlistsContent?.playlists })

  const { deletePlaylist } = useDeletePlaylist({
    playlistKey: playlistKeyToDelete,
    onSuccess: () => {
      setPlaylistKeyToDelete(undefined)
      triggerFetch()
    },
  })
  const { copyPlaylist, newPlaylistKey } = useCopyPlaylist()
  const newPlaylistKeyRef = useRef<number | null>(null)

  const {
    highlightedKey,
    setHighlightedKey,
    ref: scrollRef,
  } = useScrollToHighlight({
    items: filteredPlaylists.map((playlist) => playlist.playlistKey),
    onScroll: () => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  })

  useEffect(() => {
    if (newPlaylistKey && newPlaylistKey !== newPlaylistKeyRef.current) {
      newPlaylistKeyRef.current = newPlaylistKey
      setHighlightedKey(newPlaylistKey)
    }
  }, [newPlaylistKey, setHighlightedKey])

  const { removePlaylist } = useRemoveSharedPlaylist({
    playlistKey: sharedPlaylistToRemove?.key as number,
    userKey: user?.id?.toString() ?? '',
    email: user?.username ?? '',
    onSuccess: () => {
      setSharedPlaylistToRemove(undefined)
      triggerFetch()
    },
  })

  useMountEffect(triggerFetch)

  const navigateToPlaylistDetails = (playlistId: string) => {
    navigate(`/${basePath}/playlist-details/${playlistId}`)
  }

  if (isLoading) {
    return <LoadingProgress />
  }

  if (
    !playlistsContent?.playlists ||
    playlistsContent?.playlists.length === 0
  ) {
    return <EmptyPlaylist isLoading={false} />
  }

  return (
    <Page>
      <Box sx={{ marginBottom: theme.spacing(2) }}>
        <DropDown
          id="playlist-filter"
          menuOptions={PLAYLIST_FILTER_OPTIONS}
          defaultValue="All Playlists"
          handleSelection={(selection) => handleFilterSelection(selection)}
          variant={DropDownVariant.SortAndFilter}
          value={filterType}
        />
      </Box>
      {filteredPlaylists.length === 0 ? (
        <NoFilterResultsPlaylist />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: 1,
            margin: theme.spacing(3),
            ...(showOnDesktop && {
              margin: theme.spacing(3, 2),
            }),
          }}
        >
          {filteredPlaylists.map(
            ({ playlistKey, name, playlistType, isShared }) => (
              <Box
                key={playlistKey}
                ref={highlightedKey === playlistKey ? scrollRef : null}
                sx={{ width: 'auto', flexShrink: 0, flexGrow: 0 }}
              >
                <ContentDetailPlaylistCard
                  name={name}
                  playlistType={playlistType}
                  onClick={() =>
                    navigateToPlaylistDetails(playlistKey.toString())
                  }
                  playlistKey={playlistKey}
                  onDeleteClick={() => setPlaylistKeyToDelete(playlistKey)}
                  isShared={!!isShared}
                  onCopyPlaylist={() => copyPlaylist(playlistKey)}
                  onRemoveSharedPlaylistClick={() =>
                    setSharedPlaylistToRemove({ key: playlistKey, name })
                  }
                  highlighted={highlightedKey === playlistKey}
                />
              </Box>
            )
          )}
        </Box>
      )}
      {!!playlistKeyToDelete && (
        <DeletePlaylistConfirmationModal
          isOpen
          onCancel={() => setPlaylistKeyToDelete(undefined)}
          onConfirm={deletePlaylist}
          label="Playlist"
          confirmationMessage="This action will permanently delete the playlist and all its items. Do you wish to continue"
        />
      )}
      {!!sharedPlaylistToRemove && (
        <RemoveSharedPlaylistModal
          isOpen
          onCancel={() => setSharedPlaylistToRemove(undefined)}
          onConfirm={removePlaylist}
          playlistName={sharedPlaylistToRemove.name}
        />
      )}
    </Page>
  )
}

export default PlaylistGrid
